/** CSS mockup host for generated still + motion. No physics, no user CSS. */

import {
  coerceKind,
  coercePath,
  parseSceneSample,
  type ElementKind,
  type MotionPath,
  type SceneSample,
} from "./scene-from-still";
import { parseMotionFlow, type MotionFlow } from "./motion-flow";

export type MockupDrift = "slow" | "med" | "fast";
export type PlayLayer = "still" | "motion" | "css";

export type MediaMockup = {
  ken: number;
  vignette: number;
  grain: number;
  veil: number;
  drift: MockupDrift;
  fit: "cover" | "contain";
};

export type MediaBundle = {
  hasStill: boolean;
  hasVideo: boolean;
  hasPlate: boolean;
  hasScene?: boolean;
  hasObjects?: boolean;
  playLayer?: PlayLayer;
};

export type MotionMedia = {
  hasStill: boolean;
  videoUrl?: string;
  mockup: MediaMockup;
  scene?: SceneSample;
  object?: ElementKind;
  path?: MotionPath;
  density?: number;
  flow?: MotionFlow;
};

const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.5'/%3E%3C/svg%3E")`;

function clamp01(n: unknown, fb: number): number {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return fb;
  return Math.max(0, Math.min(1, x));
}

export function parseMediaMockup(raw: unknown): MediaMockup | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const driftRaw = String(o.drift || "");
  const drift: MockupDrift =
    driftRaw === "slow" || driftRaw === "fast" ? driftRaw : "med";
  return {
    ken: clamp01(o.ken, 0.4),
    vignette: clamp01(o.vignette, 0.45),
    grain: clamp01(o.grain, 0.22),
    veil: clamp01(o.veil, 0.16),
    drift,
    fit: o.fit === "contain" ? "contain" : "cover",
  };
}

export function mockupFromBrief(brief: string): MediaMockup {
  const t = (brief || "").toLowerCase();
  return {
    ken: /still|photo|portrait|icon/.test(t) ? 0.18 : 0.42,
    vignette: /void|night|dark|volcano|abyss|cave/.test(t) ? 0.62 : 0.42,
    grain: /film|grit|ash|dust|ember|lava|smoke/.test(t) ? 0.38 : 0.2,
    veil: /fog|mist|haze|steam/.test(t) ? 0.28 : 0.16,
    drift: /fast|explod|rush|storm/.test(t)
      ? "fast"
      : /slow|fog|mist|drift|idle/.test(t)
        ? "slow"
        : "med",
    fit: "cover",
  };
}

export function parseMotionMedia(raw: unknown): MotionMedia | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const mockup = parseMediaMockup(o.mockup) || mockupFromBrief(String(o.prompt || ""));
  const videoUrl =
    typeof o.videoUrl === "string" && /^https:\/\//i.test(o.videoUrl)
      ? o.videoUrl.slice(0, 2048)
      : undefined;
  return {
    hasStill: o.hasStill !== false,
    videoUrl,
    mockup,
    scene: parseSceneSample(o.scene),
    object: o.object != null ? coerceKind(o.object) : undefined,
    path: o.path != null ? coercePath(o.path) : undefined,
    density:
      typeof o.density === "number" && Number.isFinite(o.density)
        ? Math.max(0, Math.min(1, o.density))
        : undefined,
    flow: o.flow != null ? parseMotionFlow(o.flow, String(o.prompt || "")) : undefined,
  };
}

function dur(drift: MockupDrift): number {
  if (drift === "slow") return 28;
  if (drift === "fast") return 10;
  return 18;
}

export function compileMediaMockup(
  id: string,
  mockup: MediaMockup,
  bundle: MediaBundle,
  selector?: string,
): string {
  const safe = String(id || "fx")
    .replace(/[^a-zA-Z0-9_-]/g, "-")
    .replace(/^-+/, "x")
    .slice(0, 48);
  const sel = selector || `[data-bg-fx="${safe}"]`;
  const scale = (1 + mockup.ken * 0.2).toFixed(3);
  const shift = (mockup.ken * 2.4).toFixed(2);
  const seconds = dur(mockup.drift);
  const fit = mockup.fit === "contain" ? "contain" : "cover";
  const layer: PlayLayer = bundle.playLayer || (bundle.hasScene ? "css" : bundle.hasVideo ? "motion" : "still");
  const parts: string[] = [
    `${sel}{position:relative;isolation:isolate;overflow:hidden}`,
    `${sel} .sf-motion-host{position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:0}`,
    `@keyframes sf-ken-${safe}{from{transform:scale(1) translate(0,0)}to{transform:scale(${scale}) translate(-${shift}%,${shift}%)}}`,
    `@keyframes sf-plate-${safe}{0%,40%{opacity:.08}50%{opacity:.55}100%{opacity:.12}}`,
  ];
  if (bundle.hasStill) {
    parts.push(
      `${sel} .sf-g-still{position:absolute;inset:${layer === "still" ? "0" : "-6%"};width:100%;height:100%;object-fit:${fit};object-position:center;background-image:var(--sf-wallpaper,none);background-size:${fit};background-position:center;z-index:0;opacity:1;display:block;pointer-events:none;animation:${layer === "still" ? "none" : `sf-ken-${safe} ${seconds}s ease-in-out infinite alternate`}}`,
    );
  }
  if ((bundle.hasScene || bundle.hasObjects) && (layer === "css" || layer === "motion")) {
    parts.push(
      `${sel} .sf-g-scene{position:absolute;inset:-6%;display:grid;z-index:2;pointer-events:none}`,
      `${sel} .sf-g-cell{width:100%;height:100%;opacity:calc(0.28 + var(--den,0.4)*0.45);mix-blend-mode:soft-light;background:var(--cell,transparent)}`,
      `${sel} .sf-g-cell[data-kind=fire]{background:radial-gradient(circle at 50% 70%,rgba(255,140,40,.7),rgba(180,30,10,.05) 72%);mix-blend-mode:screen;animation:sf-el-fire ${Math.max(3, seconds / 5)}s ease-in-out infinite alternate}`,
      `${sel} .sf-g-cell[data-kind=water]{background:radial-gradient(circle at 40% 40%,rgba(80,200,255,.55),rgba(10,40,90,.04) 70%);mix-blend-mode:screen;animation:sf-el-water ${Math.max(4, seconds / 4)}s linear infinite}`,
      `${sel} .sf-g-cell[data-kind=steam]{background:radial-gradient(circle,rgba(255,255,255,.4),transparent 68%);mix-blend-mode:overlay;animation:sf-el-steam 7s ease-in-out infinite}`,
      `${sel} .sf-g-cell[data-kind=cloud]{background-image:${GRAIN};background-size:90px 90px;mix-blend-mode:soft-light;opacity:.5;animation:sf-el-cloud 16s linear infinite}`,
      `${sel} .sf-g-cell[data-kind=earth]{background:radial-gradient(circle,rgba(90,60,30,.35),transparent 70%);mix-blend-mode:multiply}`,
      `${sel} .sf-g-cell[data-kind=wind]{background:linear-gradient(90deg,transparent,rgba(220,235,255,.28),transparent);mix-blend-mode:screen;animation:sf-el-wind 5s linear infinite}`,
      `${sel} .sf-g-cell[data-kind=void]{background:rgba(0,0,0,.18);mix-blend-mode:multiply}`,
      `${sel} .sf-g-cell[data-edge=1]{mix-blend-mode:screen;opacity:.85;box-shadow:inset 0 0 14px color-mix(in srgb,var(--pf-cyan,#22d3ee) 45%,#fb923c);animation:sf-el-seam 2.4s ease-in-out infinite alternate}`,
      `${sel} .sf-g-cell[data-path=rise]{animation-name:sf-path-rise;animation-duration:${(3.2 / (0.4 + (layer === "motion" ? 0.4 : 0))).toFixed(2)}s;animation-timing-function:ease-in-out;animation-iteration-count:infinite;animation-direction:alternate}`,
      `${sel} .sf-g-cell[data-path=fall]{animation-name:sf-path-fall;animation-duration:2.6s;animation-timing-function:linear;animation-iteration-count:infinite}`,
      `${sel} .sf-g-cell[data-path=stream]{animation-name:sf-path-stream;animation-duration:3.4s;animation-timing-function:linear;animation-iteration-count:infinite}`,
      `${sel} .sf-g-cell[data-path=drift]{animation-name:sf-path-drift;animation-duration:8s;animation-timing-function:ease-in-out;animation-iteration-count:infinite;animation-direction:alternate}`,
      `${sel} .sf-g-cell[data-path=shear]{animation-name:sf-path-shear;animation-duration:4.2s;animation-timing-function:linear;animation-iteration-count:infinite}`,
      `${sel} .sf-g-cell[data-path=settle]{animation-name:sf-path-settle;animation-duration:11s;animation-timing-function:ease-in-out;animation-iteration-count:infinite;animation-direction:alternate}`,
      `@keyframes sf-path-rise{from{transform:translateY(8%) scale(1)}to{transform:translateY(-14%) scale(1.06)}}`,
      `@keyframes sf-path-fall{from{transform:translateY(-12%)}to{transform:translateY(16%)}}`,
      `@keyframes sf-path-stream{from{transform:translateX(-10%)}to{transform:translateX(12%)}}`,
      `@keyframes sf-path-drift{from{transform:translate(-4%,2%)}to{transform:translate(5%,-3%)}}`,
      `@keyframes sf-path-shear{from{transform:translateX(-18%) skewX(-4deg)}to{transform:translateX(16%) skewX(4deg)}}`,
      `@keyframes sf-path-settle{from{transform:translate(0,0) scale(1)}to{transform:translate(2%,3%) scale(1.03)}}`,
      `${sel} .sf-g-obj{position:absolute;z-index:3;pointer-events:none;will-change:transform}`,
      `${sel} .sf-g-obj[data-kind=fire]{width:8px;height:8px;border-radius:99px;background:#fb923c;box-shadow:0 0 12px #f97316}`,
      `${sel} .sf-g-obj[data-kind=water]{width:3px;height:16px;border-radius:99px;background:linear-gradient(#7dd3fc,transparent)}`,
      `${sel} .sf-g-obj[data-kind=steam],${sel} .sf-g-obj[data-kind=cloud]{width:22px;height:16px;border-radius:99px;filter:blur(5px);background:rgba(255,255,255,.55)}`,
      `${sel} .sf-g-obj[data-kind=wind]{width:28px;height:2px;background:rgba(226,232,255,.7)}`,
      `${sel} .sf-g-obj[data-kind=earth]{width:12px;height:14px;border-radius:70% 0 70% 0;background:#8fbc4a;box-shadow:0 0 8px rgba(143,188,74,.45)}`,
      `${sel} .sf-g-obj[data-kind=void]{width:10px;height:10px;border-radius:99px;background:rgba(0,0,0,.4)}`,
      `${sel} .sf-g-obj[data-path=rise]{animation-name:sf-path-rise;animation-duration:${(3.4 / (0.55 + (layer === "motion" ? 0.35 : 0))).toFixed(2)}s;animation-timing-function:ease-in-out;animation-iteration-count:infinite;animation-direction:alternate}`,
      `${sel} .sf-g-obj[data-path=fall]{animation-name:sf-path-fall;animation-duration:2.8s;animation-timing-function:linear;animation-iteration-count:infinite}`,
      `${sel} .sf-g-obj[data-path=stream]{animation-name:sf-path-stream;animation-duration:3.6s;animation-timing-function:linear;animation-iteration-count:infinite}`,
      `${sel} .sf-g-obj[data-path=drift]{animation-name:sf-path-drift;animation-duration:9s;animation-timing-function:ease-in-out;animation-iteration-count:infinite;animation-direction:alternate}`,
      `${sel} .sf-g-obj[data-path=shear]{animation-name:sf-path-shear;animation-duration:4.4s;animation-timing-function:linear;animation-iteration-count:infinite}`,
      `${sel} .sf-g-obj[data-path=settle]{animation-name:sf-path-settle;animation-duration:12s;animation-timing-function:ease-in-out;animation-iteration-count:infinite;animation-direction:alternate}`,
      `${sel} .sf-g-wisp{position:absolute;width:7px;height:7px;border-radius:99px;pointer-events:none;z-index:3;opacity:.0}`,
      `${sel} .sf-g-wisp[data-kind=fire]{background:#fb923c;box-shadow:0 0 10px #f97316;animation:sf-el-ember 2.2s ease-out infinite}`,
      `${sel} .sf-g-wisp[data-kind=water]{width:3px;height:14px;border-radius:99px;background:linear-gradient(#7dd3fc,transparent);animation:sf-el-drop 1.6s linear infinite}`,
      `${sel} .sf-g-wisp[data-kind=steam],${sel} .sf-g-wisp[data-kind=cloud]{width:18px;height:18px;filter:blur(4px);background:rgba(255,255,255,.45);animation:sf-el-steam 6s ease-in-out infinite}`,
      `${sel} .sf-g-wisp[data-kind=wind]{width:22px;height:2px;background:rgba(226,232,255,.55);animation:sf-el-wind 3.2s linear infinite}`,
      `${sel} .sf-g-wisp[data-kind=earth]{width:9px;height:6px;border-radius:40%;background:rgba(120,80,40,.5);animation:sf-el-clump 9s ease-in-out infinite}`,
      `@keyframes sf-el-fire{from{transform:scale(1);filter:brightness(1)}to{transform:scale(1.08);filter:brightness(1.25)}}`,
      `@keyframes sf-el-water{from{background-position:0 0}to{background-position:12px 18px}}`,
      `@keyframes sf-el-steam{0%{transform:translateY(8%) scale(.8);opacity:.1}50%{opacity:.55}100%{transform:translateY(-28%) scale(1.15);opacity:0}}`,
      `@keyframes sf-el-cloud{from{background-position:0 0}to{background-position:90px 40px}}`,
      `@keyframes sf-el-wind{from{transform:translateX(-30%);opacity:.1}to{transform:translateX(40%);opacity:.45}}`,
      `@keyframes sf-el-seam{from{filter:brightness(1)}to{filter:brightness(1.45)}}`,
      `@keyframes sf-el-ember{0%{transform:translateY(0) scale(1);opacity:0}20%{opacity:.9}100%{transform:translateY(-70px) scale(.3);opacity:0}}`,
      `@keyframes sf-el-drop{0%{transform:translateY(-10%);opacity:0}15%{opacity:.85}100%{transform:translateY(80%);opacity:0}}`,
      `@keyframes sf-el-clump{from{transform:translate(0,0)}to{transform:translate(4%,2%)}}`,
    );
  }
  if (bundle.hasPlate && layer !== "motion") {
    parts.push(
      `${sel} .sf-g-plate{position:absolute;inset:-10%;background-image:var(--sf-motion-plate,none);background-size:${fit};background-position:center;z-index:0;opacity:.35;animation:sf-ken-${safe} ${seconds + 6}s ease-in-out infinite alternate-reverse,sf-plate-${safe} ${Math.max(8, seconds - 4)}s ease-in-out infinite}`,
    );
  }
  if (bundle.hasVideo && (layer === "motion" || layer === "css")) {
    parts.push(
      `${sel} .sf-g-video{position:absolute;inset:0;width:100%;height:100%;object-fit:${fit};z-index:${layer === "css" ? "1" : "2"};pointer-events:none}`,
    );
  }
  if (mockup.veil > 0.02) {
    const a = Math.round(mockup.veil * 100);
    parts.push(
      `${sel} .sf-g-veil{position:absolute;inset:0;z-index:2;pointer-events:none;background:radial-gradient(ellipse at 30% 80%,color-mix(in srgb,var(--pf-purple,#a78bfa) ${a}%,transparent),transparent 58%),radial-gradient(ellipse at 78% 18%,color-mix(in srgb,var(--pf-cyan,#22d3ee) ${Math.max(8, a - 6)}%,transparent),transparent 52%);mix-blend-mode:soft-light}`,
    );
  }
  if (mockup.grain > 0.02) {
    parts.push(
      `${sel} .sf-g-grain{position:absolute;inset:0;z-index:3;pointer-events:none;opacity:${mockup.grain.toFixed(2)};background-image:${GRAIN};background-size:180px 180px;mix-blend-mode:overlay}`,
    );
  }
  if (mockup.vignette > 0.02) {
    const v = (0.35 + mockup.vignette * 0.55).toFixed(2);
    parts.push(
      `${sel} .sf-g-vignette{position:absolute;inset:0;z-index:4;pointer-events:none;box-shadow:inset 0 0 18vw rgba(0,0,0,${v})}`,
    );
  }
  parts.push(
    `@media (prefers-reduced-motion:reduce){${sel} .sf-g-still,${sel} .sf-g-plate,${sel} .sf-g-cell,${sel} .sf-g-wisp{animation:none}}`,
  );
  return parts.join("\n");
}

export function mockupSummary(bundle: MediaBundle, mockup: MediaMockup): string {
  const bits = [
    bundle.hasStill ? "still" : "",
    bundle.hasVideo ? "motion clip" : bundle.hasPlate ? "motion plate" : "",
    bundle.hasScene ? "CSS scene" : "CSS mockup",
  ].filter(Boolean);
  return `${bits.join(" · ")} · ${mockup.drift} drift`;
}
